import { useState } from 'react';

import { requestApi } from 'api/Api';

import getVideoDuration from 'lib/helpers/getVideoDuration';

import { showAlert } from 'components/shared/Alert/Alert';

import { CHUNK_SIZE, FILE_UPLOAD_STATUS } from '../../GeneralUploader/components/lib/constants';

const patchFileProgress = (setUploadProgressList, filename, patch) =>
  setUploadProgressList((prev) =>
    prev.map((p) => (p.filename === filename ? { ...p, ...patch } : p))
  );

const markFileError = (setUploadProgressList, filename) =>
  patchFileProgress(setUploadProgressList, filename, { status: FILE_UPLOAD_STATUS.ERROR });

const useVideoUploader = ({
  initiateUrl = '/api/admin/videos/upload/initiate',
  presignPartUrl = '/api/admin/videos/upload/presign',
  completeUploadUrl = '/api/admin/videos/upload/complete'
} = {}) => {
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (files = [], params = {}, setUploadProgressList = () => {}) => {
    if (!files || files.length === 0) return false;
    setUploading(true);

    // The managed key only targets the S3 destination (initiate); everything else is
    // video metadata forwarded to complete. An empty file_key must not be sent at all
    const { file_key: managedFileKey, ...metadataParams } = params;
    let allSucceeded = true;

    for (const file of files) {
      const totalParts = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

      setUploadProgressList((prev) => [
        ...prev.filter((p) => p.filename !== file.name),
        {
          filename: file.name,
          progress: 0,
          total: totalParts,
          size: file.size,
          status: FILE_UPLOAD_STATUS.INITIATED
        }
      ]);

      // read video duration (seconds) if file is a video
      let durationSeconds = 0;
      try {
        const dur = await getVideoDuration(file);
        durationSeconds = Math.round(dur) || 0;
      } catch (e) {
        durationSeconds = 0;
      }

      // Fail fast: complete rejects a missing duration anyway, but only AFTER the whole
      // file has been uploaded — and with a misleading "missing required data" message
      if (!durationSeconds) {
        showAlert({
          title: `Couldn't read the video duration from ${file.name} — the file may use a codec this browser can't play`,
          color: 'danger'
        });
        markFileError(setUploadProgressList, file.name);
        allSucceeded = false;
        continue;
      }

      let info;
      try {
        const multipartRes = await requestApi({
          url: initiateUrl,
          params: {
            filename: file.name,
            contentType: file.type,
            // Managed keys (e.g. onboarding training videos): upload to an explicit
            // file_key instead of a filename-derived one
            ...(managedFileKey && { file_key: managedFileKey })
          },
          // requestApi resolves normally on business errors (code !== 0) — without an
          // onError the new permission (2) and invalid-file-key (7) rejections would
          // fail with no feedback at all
          onError: (error) => {
            showAlert({ title: error ?? "Couldn't start the upload", color: 'danger' });
          }
        });
        info = multipartRes?.data;
      } catch (err) {
        showAlert({ title: err ?? "Couldn't start the upload", color: 'danger' });
        markFileError(setUploadProgressList, file.name);
        allSucceeded = false;
        continue;
      }

      if (!info) {
        markFileError(setUploadProgressList, file.name);
        allSucceeded = false;
        continue;
      }

      const { uploadId, key } = info;
      const parts = [];
      let didPartUploadFail = false;

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);

        let signedUrl;
        try {
          const presignRes = await requestApi({
            url: presignPartUrl,
            params: {
              key,
              uploadId,
              partNumber
            },
            onError: (error) => {
              showAlert({ title: error ?? "Couldn't upload the video", color: 'danger' });
            }
          });
          signedUrl = presignRes?.data?.signedUrl;
        } catch (err) {
          showAlert({ title: err ?? "Couldn't upload the video", color: 'danger' });
          markFileError(setUploadProgressList, file.name);
          break;
        }

        if (!signedUrl) {
          markFileError(setUploadProgressList, file.name);
          break;
        }

        try {
          const response = await fetch(signedUrl, {
            method: 'PUT',
            body: blob
          });

          if (!response.ok) {
            markFileError(setUploadProgressList, file.name);
            break;
          }

          const percent = Math.ceil((partNumber / totalParts) * 100);
          patchFileProgress(setUploadProgressList, file.name, {
            progress: percent,
            status: FILE_UPLOAD_STATUS.UPLOADING
          });

          const etag = response.headers.get('ETag')?.replace(/"/g, '');
          if (etag) parts.push({ ETag: etag, PartNumber: partNumber });
          else throw new Error(`ETag not found for part ${partNumber}`);
        } catch (err) {
          didPartUploadFail = true;
          showAlert({ title: err?.message ?? "Couldn't upload the video", color: 'danger' });
          markFileError(setUploadProgressList, file.name);
          break;
        }
      }

      let fileCompleted = false;
      if (parts.length > 0) {
        if (!didPartUploadFail && parts.length === totalParts) {
          try {
            await requestApi({
              url: completeUploadUrl,
              params: {
                key,
                uploadId,
                parts,
                duration_seconds: durationSeconds,
                title: file.name,
                ...metadataParams
              },
              onSuccess: () => {
                fileCompleted = true;
                patchFileProgress(setUploadProgressList, file.name, {
                  progress: 100,
                  status: FILE_UPLOAD_STATUS.UPLOADED
                });
                showAlert({ title: `${file.name} uploaded`, color: 'success' });
              },
              onError: (error) => {
                showAlert({
                  title: error ?? "Couldn't complete the upload",
                  color: 'danger'
                });
                markFileError(setUploadProgressList, file.name);
              }
            });
          } catch (err) {
            showAlert({ title: err?.message ?? "Couldn't complete the upload", color: 'danger' });
            markFileError(setUploadProgressList, file.name);
          }
        }
      }
      if (!fileCompleted) allSucceeded = false;
    }

    setUploading(false);
    return allSucceeded;
  };

  return { uploading, uploadFiles };
};

export default useVideoUploader;
